import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isArray } from 'lodash/fp';
import { errorToI18n, isRegularError, unwrapErrors } from 'app/lib/error';
import {
    ModalBanner,
    type ModalBannerProps,
    ModalBannerVariant
} from 'app/components/core/Modal/ModalBanner/ModalBanner';

interface I18nError {
    key: string;
    data: Record<string, unknown>;
}

type DisplayError = string | string[] | ReactNode | { _type: string; [key: string]: unknown };

export interface ModalErrorBannerProps extends ModalBannerProps {
    error: DisplayError;
    mapToI18n?: (error: { _type: string; [key: string]: unknown }) => I18nError | undefined;
}

const DEFAULT_FN = errorToI18n();

export function ModalErrorBanner({
    error,
    mapToI18n = DEFAULT_FN,
    ...props
}: ModalErrorBannerProps) {
    const { t } = useTranslation();

    let translatedErrorOrErrors: ReactNode;

    if (isRegularError(error)) {
        const translateError = (error: { _type: string; [key: string]: unknown }) => {
            const i18nError = mapToI18n(error);
            if (!i18nError) return '';
            return t(i18nError.key, i18nError.data);
        };
        const errorOrErrors = unwrapErrors(error);
        translatedErrorOrErrors = isArray(errorOrErrors)
            ? errorOrErrors.map(translateError)
            : translateError(errorOrErrors);
    } else {
        translatedErrorOrErrors = error as ReactNode;
    }

    return (
        <ModalBanner variant={ModalBannerVariant.ERROR} {...props}>
            {translatedErrorOrErrors}
        </ModalBanner>
    );
}
